import { ApiError } from '@fondationcss/sdk';

/**
 * The message the server wrote in the body of its error response, or `fallback` if it wrote
 * none.
 *
 * The SDK's default error handler only displays the status — « La requête a échoué : erreur
 * HTTP 409 » — whereas the body often carries the real reason, in French, written by the guard
 * that refused. The Parcours modules refuse in three ways:
 * `Results.Conflict(message)` (bare string), `Results.Conflict(new { message })`
 * and `Results.Conflict(new { error })`; ProblemDetails adds `detail`/`title`.
 *
 * Only a 4xx body is trusted: it is a refusal the server phrased for the user. A 5xx body is a
 * fault report — in Development the ASP.NET exception page, with the stack trace and the raw
 * request headers, session cookie included (QA #722, N1) — and must never reach the screen.
 *
 * To be used with `{ errorHandler: null }` on the call, otherwise two bubbles appear:
 * the SDK's (generic) then the caller's (explicit).
 */
export function serverMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  if (!isClientRefusal(err.httpStatus)) return fallback;

  const content: unknown = err.content;
  // No status means no answer: the SDK wraps what `fetch` or `JSON.parse` threw — a TypeError
  // « Failed to fetch », a SyntaxError, an AbortError — as the content. That is the browser's
  // English text, not a refusal the server phrased (#1090).
  if (err.httpStatus === undefined && isThrownError(content)) return fallback;
  if (typeof content === 'string') {
    const text = content.trim();
    return text && !isFaultReport(text) ? text : fallback;
  }
  if (content && typeof content === 'object') {
    for (const key of ['message', 'error', 'detail', 'title'] as const) {
      const value = (content as Record<string, unknown>)[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
  }
  return fallback;
}

/**
 * The ASP.NET exception page, whatever its status. A 4xx is not always a guard's refusal: the
 * framework answers 400 itself when a body does not bind (an empty date sent as `""` for a
 * DateOnly), and in Development that answer is the same page as a 5xx — exception type, stack
 * frames, the raw request headers and the session cookie. It reached the screen from « Créer des
 * groupes » (#1188 review).
 */
export function isFaultReport(text: string): boolean {
  return /^[A-Za-z_][\w.`]*Exception\b/.test(text)
    || /(^|\n)\s*at [\w.<>`$|]+[.(]/.test(text)
    || /\bHEADERS\s*=+/.test(text);
}

/** 4xx, or an unknown status (the SDK could not tell): the body may be shown. */
export function isClientRefusal(httpStatus: number | undefined): boolean {
  return httpStatus === undefined || (httpStatus >= 400 && httpStatus < 500);
}

/**
 * The server's refusal of an individual pathway for want of a calendar: no calendar was sent and
 * the program has no delivery to take one from (#1437). The one 400 of that route a person can fix,
 * by choosing a calendar; the others carry their own sentence. Mirrors `IndividualPlanningRefusal`.
 */
export function isCalendarRequired(err: unknown): boolean {
  return err instanceof ApiError && err.httpStatus === 400
    && (err.content as { code?: unknown } | undefined)?.code === 'calendarRequired';
}

/**
 * A 409: the server refused because what the screen showed changed since it was read — a
 * competency that lost its teacher or got locked while a dialog was open (#971). The screen then
 * reads it again rather than offering the same refused choice.
 */
export function isConflict(err: unknown): boolean {
  return err instanceof ApiError && err.httpStatus === 409;
}

/**
 * Why a read failed, as far as the client can tell (#1092, #1329).
 *
 * One fallback for every cause leaves the user unable to tell a refusal from a record that is
 * gone, or from a server fault. A route whose 404 can mean something else refines `missing`
 * itself: the availability reads also answer a bare 404 when their feature is switched off
 * (teacher-annual-load-failure.ts).
 */
export type LoadFailureKind = 'offline' | 'refused' | 'missing' | 'failed';

export function loadFailureKind(err: unknown): LoadFailureKind {
  if (!(err instanceof ApiError)) return 'failed';
  // No status: the SDK wraps both a fetch that got no answer (TypeError) and a 200 whose body is
  // not JSON (SyntaxError, a proxy or SPA page). Only the first is the network.
  if (err.httpStatus === undefined) return err.content instanceof SyntaxError ? 'failed' : 'offline';
  if (err.httpStatus === 403) return 'refused';
  if (err.httpStatus === 404) return 'missing';
  return 'failed';
}

/** An error object thrown by the browser. DOMException is tested apart: older engines do not derive it from Error. */
function isThrownError(content: unknown): boolean {
  return content instanceof Error
    || (typeof DOMException !== 'undefined' && content instanceof DOMException);
}
